import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './tech-stack-modal.css';

const TechStackModal = ({ isOpen, onClose, techStack }) => {
  const modalRef = useRef(null);

  // Prevent scrolling on body when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Close modal on escape key press
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };

    window.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Handle wheel events on modal content
  useEffect(() => {
    const modalElement = modalRef.current;

    if (isOpen && modalElement) {
      const handleWheel = (e) => {
        e.stopPropagation();
      };

      modalElement.addEventListener('wheel', handleWheel, { passive: true });

      return () => {
        modalElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, [isOpen]);

  // Early return if modal is not open or no tech stack provided
  if (!isOpen || !techStack) return null;

  // Simplified modal animations with no springs
  const modalVariants = {
    hidden: {
      opacity: 0,
      y: 10,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
    exit: {
      opacity: 0,
      y: 10,
      transition: {
        duration: 0.15,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.15 },
    },
  };

  // Generate a unique icon for each tech category
  const getCategoryIcon = (category) => {
    const icons = {
      Frontend: '🎨',
      'Frontend Framework': '⚛️',
      Backend: '⚙️',
      Database: '💾',
      DevOps: '🚀',
      Mobile: '📱',
      Testing: '🧪',
      Design: '✏️',
      Languages: '📝',
      Tools: '🔧',
      Frameworks: '🔌',
      Libraries: '📚',
      'Core Technologies': '⚛️',
      'State Management': '🔄',
      Navigation: '🧭',
      'UI Libraries': '🧩',
      'UI & Styling': '💅',
      Routing: '🛣️',
      'API Communication': '📡',
      'Form Handling': '📋',
      'Networking & Data': '🌐',
      'Device Features': '📲',
      Internationalization: '🌍',
      'Development Tools': '🛠️',
      'Project Architecture': '🏗️',
      'Other Notable Libraries': '📚',
      'Maps Integration': '🗺️',
      'Data Visualization': '📊',
      'Notification Handling': '🔔',
      'Build & Development Tools': '⚒️',
    };

    return icons[category] || '💻';
  };

  // Prevent clicks inside the modal from closing it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Prevent wheel events from bubbling up to parent
  const handleWheel = (e) => {
    e.stopPropagation();
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="tech-stack-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
          onWheel={handleWheel}
        >
          <motion.div
            ref={modalRef}
            className="tech-stack-modal"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleModalClick}
            onWheel={handleWheel}
            style={{ willChange: 'transform, opacity' }}
          >
            <div className="tech-stack-modal-header">
              <h2 className="tech-stack-title">Technology Stack</h2>
              <motion.button
                className="close-button"
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                &times;
              </motion.button>
            </div>

            <div className="tech-stack-content">
              {Object.entries(techStack).map(([category, technologies]) => (
                <div key={category} className="tech-category">
                  <h3>
                    {getCategoryIcon(category)} {category}
                  </h3>
                  <ul>
                    {technologies.map((tech, index) => (
                      <motion.li
                        key={index}
                        whileHover={{
                          y: -3,
                          transition: { duration: 0.15 },
                        }}
                      >
                        <span className="tech-name">{tech.name}</span>
                        {tech.description && <span className="tech-description">{tech.description}</span>}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TechStackModal;
